import { useState } from 'react';

import { useCreatePerson, usePeople, useResetPersonPassword, useSetPersonStatus } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatDate } from 'src/lib/time';
import type { Person, UserRole } from 'src/types/editorial';

/** Answers: who can get in, and what may they do? */

const ROLE_LABEL: Record<UserRole, string> = { admin: 'Administra', editor: 'Edita' };

export function PeopleWorkspace() {
  const people = usePeople();
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  return (
    <div className="page-medium">
      <PageHeader title="Personas">
        Quién puede entrar a Pressentia. Las contraseñas las genera el sistema y se muestran una sola vez: cópiala antes de
        cerrar el aviso. Quien la pierda necesita que se la restablezcas.
      </PageHeader>

      {issued && (
        <Notice tone="supported" title={`Contraseña de ${issued.email}`} role="alert">
          <p className="people__secret mono">{issued.password}</p>
          <p>No se guarda en texto y no vuelve a mostrarse. Entrégasela directamente.</p>
          <p className="notice__action">
            <Button onClick={() => setIssued(null)}>Ya la copié</Button>
          </p>
        </Notice>
      )}

      {people.isPending && <Skeleton variant="rows" count={4} />}
      {people.isError && <LoadFailure title="No se pudo cargar la lista" error={people.error} onRetry={people.refetch} />}

      {people.data && (
        <ul className="people">
          {people.data.users.map(person => (
            <PersonRow key={person.userId} person={person} onIssued={setIssued} />
          ))}
        </ul>
      )}

      {/* The roster answers the screen's question; adding someone is what you do about the answer.
          Composition §3c caught the same inversion on Processing, which used to open on its form. */}
      <NewPerson onIssued={setIssued} />
    </div>
  );
}

function NewPerson({ onIssued }: { onIssued: (issued: { email: string; password: string }) => void }) {
  const create = useCreatePerson();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('editor');

  return (
    <form
      className="people-form"
      onSubmit={event => {
        event.preventDefault();
        create.mutate(
          { name: name.trim(), email: email.trim(), role },
          {
            onSuccess: result => {
              onIssued({ email: result.user.email, password: result.password });
              setName('');
              setEmail('');
              setRole('editor');
            },
          }
        );
      }}
    >
      <p className="people-form__heading">Dar de alta</p>

      <label className="people-form__field">
        <span>Nombre</span>
        <input type="text" value={name} onChange={event => setName(event.target.value)} required minLength={2} placeholder="Zaida Márquez" />
      </label>

      <label className="people-form__field">
        <span>Correo</span>
        <input type="email" value={email} onChange={event => setEmail(event.target.value)} required placeholder="zaida@pressentia.mx" />
      </label>

      <label className="people-form__field">
        <span>Puede</span>
        <select value={role} onChange={event => setRole(event.target.value as UserRole)}>
          <option value="editor">Edita — revisa transmisiones y notas</option>
          <option value="admin">Administra — además da de alta personas</option>
        </select>
      </label>

      <Button type="submit" variant="primary" busy={create.isPending}>
        Crear
      </Button>

      {create.isError && (
        <Notice tone="contradicted" title="No se pudo crear" role="alert">
          <p>{create.error instanceof Error ? create.error.message : 'Error desconocido'}</p>
        </Notice>
      )}
    </form>
  );
}

function PersonRow({ person, onIssued }: { person: Person; onIssued: (issued: { email: string; password: string }) => void }) {
  const reset = useResetPersonPassword();
  const setStatus = useSetPersonStatus();
  const active = person.status === 'active';

  return (
    <li className="person" data-status={person.status}>
      <div className="person__who">
        <p className="person__name">{person.name}</p>
        <p className="person__email mono">{person.email}</p>
      </div>

      <div className="person__standing">
        <Badge tone={active ? 'supported' : 'unresolved'}>{active ? ROLE_LABEL[person.role] : 'Sin acceso'}</Badge>
        <span className="person__since">desde {formatDate(person.createdAt)}</span>
      </div>

      <div className="person__actions">
        <Button
          variant="ghost"
          busy={reset.isPending}
          onClick={() => reset.mutate(person.userId, { onSuccess: result => onIssued({ email: person.email, password: result.password }) })}
        >
          Nueva contraseña
        </Button>
        {/* Withdrawing access and restoring it are the same control because they are the same
            decision reversed. Nothing here deletes: past reviews and queued jobs name this person. */}
        <Button
          variant="ghost"
          className={active ? 'person__withdraw' : undefined}
          busy={setStatus.isPending}
          onClick={() => setStatus.mutate({ userId: person.userId, status: active ? 'inactive' : 'active' })}
        >
          {active ? 'Retirar acceso' : 'Restituir'}
        </Button>
      </div>

      {setStatus.isError && (
        <p className="person__error">{setStatus.error instanceof Error ? setStatus.error.message : 'No se pudo cambiar'}</p>
      )}
    </li>
  );
}
